import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X, Download, Search } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { uid, http, apiError, money, byPatientId } from "@/lib/api";
import { exportCsv, exportRows } from "@/lib/exporters";
import { toast } from "sonner";
import Pager from "@/components/Pager";

const PAGE_SIZE = 15;
const emptyPatient = () => ({ id: uid(), name: "", gender: "", age: "", phone: "", address: "", notes: "", next_appointment: "", consent: true });

export default function Directory() {
  const { data, save, isOwner, showMoney, pullAll } = useApp();
  const [q, setQ] = useState("");
  const [gender, setGender] = useState("");
  const [form, setForm] = useState(null);
  const [ticked, setTicked] = useState([]);
  const [editing, setEditing] = useState(null); // { id, field, value }

  const startEdit = (patient, field) => setEditing({ id: patient.id, field, value: patient[field] ?? "" });

  const commitEdit = async () => {
    if (!editing) return;
    const patient = data.patients.find((p) => p.id === editing.id);
    if (!patient) return setEditing(null);
    const raw = editing.value;
    const next = editing.field === "age" ? (raw === "" ? null : Number(raw)) : String(raw).trim();
    if (editing.field === "name" && !next) {
      setEditing(null);
      return toast.error("Full Name is required");
    }
    if ((patient[editing.field] ?? "") === (next ?? "")) return setEditing(null);
    try {
      await save("patients", { ...patient, [editing.field]: next });
      setEditing(null);
      toast.success("Patient updated");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const EditableCell = ({ patient, field, display, className = "" }) => {
    const active = editing && editing.id === patient.id && editing.field === field;
    if (active)
      return (
        <input autoFocus data-testid={`dir-edit-${field}-input`} type={field === "age" ? "number" : "text"}
          className="dm-input !px-2 !py-1 !text-sm" value={editing.value}
          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitEdit();
            if (e.key === "Escape") setEditing(null);
          }} />
      );
    return (
      <button type="button" data-testid={`dir-edit-${field}-${patient.id}`} onClick={() => startEdit(patient, field)}
        className={`w-full text-left transition-colors hover:text-slate-900 ${className}`} title="Tap to edit">
        {display}
      </button>
    );
  };

  const [page, setPage] = useState(1);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return data.patients
      .filter((p) => (gender ? p.gender === gender : true))
      .filter((p) => !t || (p.name || "").toLowerCase().includes(t) || (p.phone || "").includes(t) || String(p.code ?? "") === t)
      .sort(byPatientId);
  }, [data.patients, q, gender]);

  useEffect(() => setPage(1), [q, gender]);
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stat = (id) => {
    const v = data.visits.filter((x) => x.patient_id === id);
    return { visits: v.length, total: v.reduce((a, x) => a + Number(x.clinic_share || 0), 0) };
  };

  const tick = (id) =>
    setTicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-2)));

  const doMerge = async () => {
    try {
      await http.post("/patients/merge", { keep_id: ticked[0], duplicate_id: ticked[1] });
      await pullAll();
      setTicked([]);
      toast.success("Patients merged");
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Full Name is required");
    await save("patients", { ...form, age: form.age ? Number(form.age) : null });
    toast.success("Patient added to directory");
    setForm(null);
  };

  const exportRowsData = () =>
    rows.map((p) => ({ ID: p.code ?? "", Name: p.name, Age: p.age ?? "", Gender: p.gender || "",
      Phone: p.phone || "", Visits: stat(p.id).visits, ...(showMoney ? { Total: stat(p.id).total } : {}) }));

  return (
    <div className="space-y-5" data-testid="directory-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="dm-page-title">
            Patient <span className="font-extrabold">directory</span>{" "}
            <span data-testid="dir-count" className="font-mono text-lg text-slate-500">{rows.length}</span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {isOwner && <button data-testid="dir-export-csv" onClick={() => exportCsv(exportRowsData(), "directory")} className="dm-btn-ghost"><Download size={14} /> CSV</button>}
          {isOwner && <button data-testid="dir-export-excel" onClick={() => exportRows(exportRowsData(), "directory", "Directory")} className="dm-btn-ghost"><Download size={14} /> Excel</button>}
          <button data-testid="dir-add-btn" onClick={() => setForm(emptyPatient())} className="dm-btn-primary"><Plus size={15} /> Add patient</button>
        </div>
      </div>


      {ticked.length > 0 && isOwner && (
        <div className="dm-panel flex items-center justify-between gap-3 px-4 py-3" data-testid="merge-bar">
          <span className="text-sm text-slate-700" data-testid="merge-bar-text">
            {ticked.length === 1 ? "Select one more to merge" : `Merging ${data.patients.find((p) => p.id === ticked[1])?.name} into ${data.patients.find((p) => p.id === ticked[0])?.name}`}
          </span>
          <span className="flex gap-2">
            <button data-testid="merge-go-btn" disabled={ticked.length < 2} onClick={doMerge} className="dm-btn-primary disabled:opacity-40">Merge</button>
            <button onClick={() => setTicked([])} className="dm-btn-quiet"><X size={15} /></button>
          </span>
        </div>
      )}

      <div className="dm-panel overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="relative min-w-[220px] flex-1">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="dir-search" className="dm-input !pl-9" placeholder="Search by name, phone or Patient ID…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="dir-gender" aria-label="Gender" className="dm-input !w-auto min-w-[150px]" value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">All genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
          <p className="w-full text-xs text-slate-500 lg:w-auto lg:pl-2">Click a name, age or phone to edit it. Tick two patients to merge them.</p>
        </div>
        {rows.length === 0 ? (
          <div className="dm-empty m-4" data-testid="dir-empty">No patients found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="dm-table" data-testid="dir-table">
              <thead>
                <tr>
                  {isOwner && <th />}
                  <th>ID</th><th>Name</th><th>Age</th><th>Gender</th><th>Phone</th>
                  <th className="!text-right">Visits</th><th className="!text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((p) => {
                  const s = stat(p.id);
                  return (
                    <tr key={p.id} data-testid={`dir-row-${p.id}`}>
                      {isOwner && (
                        <td className="w-9">
                          <input type="checkbox" data-testid={`dir-tick-${p.id}`} checked={ticked.includes(p.id)} onChange={() => tick(p.id)}
                            className="h-4 w-4 accent-[color:var(--dm-accent)]" />
                        </td>
                      )}
                      <td className="font-mono text-slate-500">{p.code ?? "—"}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <EditableCell patient={p} field="name" display={p.name} className="font-semibold text-slate-800" />
                          <Link to={`/app/patients/${p.id}`} data-testid={`dir-open-${p.id}`} className="dm-link shrink-0 text-xs">open</Link>
                        </div>
                      </td>
                      <td><EditableCell patient={p} field="age" display={p.age ?? "—"} /></td>
                      <td className="capitalize">{p.gender || "—"}</td>
                      <td className="font-mono"><EditableCell patient={p} field="phone" display={p.phone || "No phone"} /></td>
                      <td className="num text-slate-800">{s.visits}</td>
                      <td className="num text-slate-800">{showMoney ? money(s.total) : "•••"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pager page={page} pageSize={PAGE_SIZE} total={rows.length} onChange={setPage} testid="dir-pager" />
      </div>

      {form && (
        <div className="fixed inset-0 z-40 grid place-items-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm">
          <form onSubmit={submit} data-testid="add-patient-modal" className="dm-panel w-full max-w-lg bg-white p-6" style={{ boxShadow: "0 8px 32px rgba(0,0,0,.55)" }}>
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-head text-xl font-bold text-slate-900">Add New Patient</h3>
              <button type="button" onClick={() => setForm(null)} data-testid="add-patient-close" className="dm-btn-quiet !p-2">×</button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <p className="label-xs mb-1.5">Full Name *</p>
                <input data-testid="m-name" className="dm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <p className="label-xs mb-1.5">Gender</p>
                <select data-testid="m-gender" className="dm-input" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                  <option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                </select>
              </div>
              <div>
                <p className="label-xs mb-1.5">Age</p>
                <input type="number" data-testid="m-age" className="dm-input" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <p className="label-xs mb-1.5">Next appointment</p>
                <input type="date" data-testid="patient-appointment-input" className="dm-input" value={form.next_appointment || ""} onChange={(e) => setForm({ ...form, next_appointment: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <p className="label-xs mb-1.5">Phone</p>
                <input data-testid="m-phone" className="dm-input font-mono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} className="dm-btn-ghost">Cancel</button>
              <button type="submit" data-testid="m-save" className="dm-btn-primary">✓ Add Patient</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
